import classNames from 'classnames';
import { FC, useEffect, useMemo, useState } from 'react';

import { FAQArticle } from '../../interfaces/FAQ';
import FAQService from '../../services/FAQService';
import { useFormatting } from '../../services/hooks/useFormatting';
import styles from './FAQ.module.scss';

export interface FAQItemProps {
  faq: FAQArticle;
  searchTexts: string[];
}

const highlightTexts = (title: string, searchTexts: string[]) => {
  return searchTexts.reduce((acc, searchText) => {
    return FAQService.highlightText(acc, searchText);
  }, title);
};

export const FAQItemComponent: FC<FAQItemProps> = ({ faq, searchTexts }) => {
  const { formattedText: title } = useFormatting(highlightTexts(faq.title, searchTexts));
  const article = useMemo(() =>
    FAQService.wrapInParagraph(
      highlightTexts(faq.articleBody, searchTexts)
    ), [faq.articleBody, searchTexts]
  );
  const hasSearchTexts = searchTexts.length > 0;
  const [expanded, setExpanded] = useState(hasSearchTexts);

  useEffect(() => {
    if (hasSearchTexts){
      setExpanded(true);
    } else {
      setExpanded(false);
    }
  }, [hasSearchTexts]);

  return (
    <div
      className={styles.faqListItem}
    >
      <button
        aria-expanded={expanded}
        className={classNames(styles.faqListItemRow, {
          [styles.faqListItemRowSearchResult]: hasSearchTexts,
        })}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setExpanded(!expanded);
            e.preventDefault();
          }
        }}
      >
        <h3
          className={classNames(styles.faqListItemTitle, {
            [styles.faqListItemTitleSearchResult]: hasSearchTexts,
          })}
          dangerouslySetInnerHTML={{ __html: title }}
        />

        <img alt={expanded ? 'collapse icon' : 'expand icon'} src={`/assets/${expanded ? 'minus' : 'plus'}.svg`} />
      </button>
      <div
        className={classNames(styles.faqArticle, {
          [styles.expanded]: expanded,
          [styles.faqArticleSearchResult]: hasSearchTexts,
        })}
        dangerouslySetInnerHTML={{ __html: article }}
      />
    </div>
  );
};
